import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Pagination from './pagination';
import './rev.css'

const apiUrl = process.env.REACT_APP_API_URL;

const fetchAllArticlesByCategory = async (categoryId) => {
  let allArticles = [];
  let currentPage = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await fetch(`${apiUrl}/api/news?category_id=${categoryId}&page=${currentPage}&per_page=15`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch page ${currentPage} for category ${categoryId}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      allArticles = [...allArticles, ...data.data];
      hasMore = data.pagination && data.pagination.has_more;
      currentPage++;
    } else {
      hasMore = false;
    }
    
    // Safety break to prevent infinite loops
    if (currentPage > 20) {
      console.warn(`Stopped fetching after 20 pages for category ${categoryId}`);
      break;
    }
  }

  return { success: true, data: allArticles };
};

const deleteArticle = async (articleId) => {
  const response = await fetch(`${apiUrl}/api/news/${articleId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete article');
  }
  
  return response.json();
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, articleTitle, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="delete-modal-overlay">
      <div className="delete-modal-content">
        <div className="delete-modal-icon">
          ⚠️
        </div>
        
        <h3 className="delete-modal-title">
          تأكيد الحذف
        </h3>
        
        <p className="delete-modal-subtitle">
          هل أنت متأكد من أنك تريد حذف هذا المقال؟
        </p>
        
        <div className="delete-modal-article-title">
          <strong>
            "{articleTitle?.length > 60 ? `${articleTitle.substring(0, 60)}...` : articleTitle}"
          </strong>
        </div>
        
        <p className="delete-modal-warning">
          لا يمكن التراجع عن هذا الإجراء
        </p>
        
        <div className="delete-modal-actions">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="delete-modal-cancel"
          >
            إلغاء
          </button>
          
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="delete-modal-confirm"
          >
            {isDeleting ? (
              <>
                <div className="delete-spinner"></div>
                جاري الحذف...
              </>
            ) : (
              'حذف المقال'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewPage = ({ setActiveTab, onEditArticle, onArticleUpdated }) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [deleteModal, setDeleteModal] = React.useState({ isOpen: false, articleId: null, articleTitle: '' });
  const perPage = 10;
  const queryClient = useQueryClient();

  const reviewQuery = useQuery({
    queryKey: ['articles', 'category', 4],
    queryFn: () => fetchAllArticlesByCategory(4),
    keepPreviousData: true,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteArticle,
    onSuccess: () => {
      queryClient.invalidateQueries(['articles', 'category', 4]);
      setDeleteModal({ isOpen: false, articleId: null, articleTitle: '' });
    },
    onError: (error) => {
      alert(`خطأ في حذف المقالة: ${error.message}`);
      setDeleteModal({ isOpen: false, articleId: null, articleTitle: '' });
    },
  });

  const refreshArticleData = React.useCallback(() => {
    queryClient.invalidateQueries(['articles', 'category', 4]);
  }, [queryClient]);

  React.useEffect(() => {
    if (onArticleUpdated) {
      window.refreshArticleData = refreshArticleData;
    }
  }, [onArticleUpdated, refreshArticleData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDeleteClick = (articleId, articleTitle) => {
    setDeleteModal({
      isOpen: true,
      articleId,
      articleTitle
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.articleId) {
      deleteMutation.mutate(deleteModal.articleId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, articleId: null, articleTitle: '' });
  };

  const handleEdit = (article) => {
    if (onEditArticle) {
      onEditArticle(article, refreshArticleData); 
    }
  };

  // Get paginated articles
  const getArticles = () => {
    if (!reviewQuery.data?.data) return { articles: [], totalPages: 0 };
    
    const allArticles = reviewQuery.data.data;
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    const paginatedArticles = allArticles.slice(startIndex, endIndex);
    const totalPages = Math.ceil(allArticles.length / perPage);
    
    return { articles: paginatedArticles, totalPages, totalCount: allArticles.length };
  };

  const { articles, totalPages, totalCount } = getArticles();
  const { isLoading, isError, error, isFetching } = reviewQuery;

  if (isLoading) {
    return (
      <div className="articles-container">
        <div className="main-header">
          <h2 className="main-title">
            مراجعة الأخبار
          </h2>
          <p className="main-subtitle">
            عرض وإدارة جميع المقالات في قسم المراجعة
          </p>
        </div>

        <div className="card mb-5">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            
            <h4 className="loading-title">
              جاري تحميل مقالات المراجعة...
            </h4>
            
            <p className="loading-subtitle">
              يرجى الانتظار
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="articles-container">
        <div className="main-header">
          <h2 className="main-title">
            مراجعة الأخبار
          </h2>
        </div>
        
        <div className="card mb-5">
          <div className="alert alert-danger">
            <p>{error?.message || 'حدث خطأ في تحميل المقالات'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="articles-container">
      <div className="main-header">
        <h2 className="main-title">
          مراجعة الأخبار
        </h2>
        <p className="main-subtitle">
          عرض وإدارة جميع المقالات في قسم المراجعة
        </p>
      </div>

      {/* Button to open detailed review in new tab */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <button 
          onClick={() => window.open('/detailed-review', '_blank')}
          className="btn btn-primary"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px', marginRight: '0.5rem' }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
          مراجعة تفصيلية
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', marginLeft: '0.5rem' }}>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15,3 21,3 21,9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </button>
      </div>

      <div className="category-header">
        <h3 className="category-title">
          مراجعة
        </h3>
        <p className="category-count">
          ({totalCount || 0} مقالة إجمالي، {articles.length} معروض)
        </p>
      </div>
      
      <div className="card mb-5">
        {(isFetching || deleteMutation.isLoading) && (
          <div className="updating-indicator">
            <small className="text-muted">
              {/* {deleteMutation.isLoading ? 'جاري الحذف...' : 'جاري التحديث...'} */}
            </small>
          </div>
        )}
        
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>العنوان</th>
                <th>الأولوية</th>      
                <th> مضاف إلى شريط</th> 
                <th>التاريخ</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4"> 
                    لا توجد مقالات في قسم المراجعة حالياً
                  </td>
                </tr>
              ) : (
                articles.map((article) => {
                  return (
                    <tr key={article.id}>
                      <td>
                       <div className="article-title" title={article.title}>
                      {article.title?.length > 50 
                        ? `${article.title.substring(0, 50).trim()}...` 
                        : article.title || 'بدون عنوان'
                      }
                    </div>
                      </td>
                      <td>
                        {article.priority != null ? article.priority : '-'}  
                      </td>
                      <td>
                        {article.add_to_tinker === 1 ? 'نعم' : 'لا'}
                      </td>
                      <td>
                        {article.published_at 
                          ? new Date(article.published_at).toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' })
                          : article.created_at 
                          ? new Date(article.created_at).toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' })
                          : '-'}
                      </td>
                      <td>
                        <div className="action-links">
                          <a href="#" className="action-link edit" onClick={(e) => { 
                            e.preventDefault(); 
                            handleEdit(article);
                          }}>
                            تعديل
                          </a>
                          <a 
                            href="#" 
                            className={`action-link delete ${deleteMutation.isLoading ? 'disabled' : ''}`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!deleteMutation.isLoading) {
                                handleDeleteClick(article.id, article.title);
                              }
                            }}
                          >
                            حذف
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        articleTitle={deleteModal.articleTitle}
        isDeleting={deleteMutation.isLoading}
      />
    </div>
  );
};

export default ReviewPage;